import { Component, Inject, OnInit } from '@angular/core';
import { MODAL_DATA, NgbActiveModal } from '@agilent/ui-element';
import { User, UserApiService } from '@app/api/user-api.service';
import { FormControl, FormGroup, Validators, FormBuilder} from '@angular/forms';
import {UserPrivilegeEnum, UserService} from '@app/user.service';
import { CustomValidators } from '@app/shared/custom-input-validators';
import Utils from '@app/utils/utils';
import {NotificationService} from '@agilent/ui-element';
import {RolesApiService} from '@app/api/roles-api.service';
import {CustomerApiService} from '@app/api/customer-api.service';

@Component({
    selector: 'app-add-user',
    template: `
        <!-- TODO Address warnings in the browser console -->
        <form *ngIf="userForm" [formGroup]="userForm">
            <ag-dialog [header]=this.dialogTitle>
                <ag-dialog-content>
                    <app-input-wrapper
                            formControlName="full_name"
                            label="Full Name"
                            id="userName-field"
                            [disabled]="!canManageCurrentCustomerUsers && userData.id !== authUser.id"
                            [required]="isMandatory"></app-input-wrapper>
                    <app-input-wrapper
                            formControlName="email"
                            label="Email"
                            id="userEmail-field"
                            [required]="isMandatory"></app-input-wrapper>
                    <table>
                        <tr *ngFor="let role of roles.controls; let i=index;">
                            <td *ngIf="i==0"
                                [attr.rowspan]="roles.controls.length"
                                style="vertical-align:top; padding-right:10px; padding-top:7px">
                                Roles:
                            </td>
                            <td style="vertical-align:top;">
                                <ag-checkbox
                                        *ngIf="!canManageCurrentCustomerUsers"
                                        class="disabled checkbox-disabled"
                                        tabindex="-1"
                                        [disabled]="!canManageCurrentCustomerUsers"
                                        [ngbTooltip]="'Access denied'"
                                        [formControl]="role"
                                        [label]="user.roles[i].user_role_name"></ag-checkbox>
                                <ag-checkbox
                                        *ngIf="canManageCurrentCustomerUsers"
                                        [formControl]="role"
                                        [label]="user.roles[i].user_role_name"></ag-checkbox>
                            </td>
                        </tr>
                    </table>
                </ag-dialog-content>
                <ag-dialog-actions>
                    <span *ngIf="isLoading ">Please wait...</span>
                    <ag-loader class="inline-progress" [visible]="isLoading "
                               [small]="true"></ag-loader>
                    <ag-button
                            id="reset-password-btn"
                            [hidden]="!this.modalData.existingUser"
                            type="special"
                            [disabled]="isLoading || (!this.canManageCurrentCustomerUsers && (this.userData.id !== this.authUser.id))"
                            (click)="resetPassword()"
                            value="Reset User Password"></ag-button>
                    <ag-button
                            type="secondary"
                            [disabled]="isLoading"
                            id="cancel-btn"
                            (click)="cancel()"
                            value="Cancel"></ag-button>
                    <ag-button
                            [disabled]="isSaveButtonDisabled()"
                            [class.disabled]="isSaveButtonDisabled()"
                            [tooltipContent]="checkIfNewRolesLockOutUser() ? 'Select at least one role with access to manage users' : ''"
                            id="save-btn"
                            type="primary"
                            (click)="save()"
                            [value]="addUpdateBtnLabel"></ag-button>
                </ag-dialog-actions>
            </ag-dialog>
        </form>
    `,
    styles: [`
        :host ::ng-deep ag-label, .label {
            width: 20%;
        }

        ag-select, ag-input {
            margin: 1rem 0;
        }

        :host .inline-progress > ::ng-deep .loader-overlay {
            background: transparent !important;
            margin: 0;
            padding: 0;
            position: static;
            transform: scale(0.7);
        }
        
        .checkbox-disabled {
            position: relative;
            cursor: not-allowed;
        }
        
        .checkbox-disabled::after {
            content: '';
            position: absolute;
            top: -60%;
            bottom: -25%;
            left: -1%;
            right: -1%;
            background-color: transparent;
            z-index: 1;
        }
    `]
})

export class AddUserComponent implements OnInit {

    SYSTEM_CUSTOMER_ID = 0;
    isMandatory = true;
    isEditing = false;
    userData: User = {
        is_superuser: false,
        id: -1,
        customer_id: 0,
        is_deleted: false,
        full_name: '',
        email: '',
        user_prefs: {},
        user_privileges: '',
        user_roles: {}
    };
    addUpdateBtnLabel = 'Save';
    dialogTitle = 'Create User';
    canManageCustomerUsers = false;
    canManageSystemUsers = false;
    canManageCurrentCustomerUsers = false;
    isLoading: boolean;
    customerId: number;
    userForm: FormGroup;
    user = {
        roles: []
    };
    authUser: User;
    allRoles = {};
    selectedRoleIds = [];
    rolesPrivilegesMatrix: number[][] = [];
    userAssignedRoles = [];
    constructor(
        private activeModal: NgbActiveModal,
        private userApiService: UserApiService,
        private userService: UserService,
        private formBuilder: FormBuilder,
        private notificationService: NotificationService,
        private rolesApiService: RolesApiService,
        @Inject(MODAL_DATA) public modalData: any
    ) {
        this.customerId = modalData.customerId;

        // First check if we have data we need to edit
        if (modalData.existingUser) {

            // Make a copy of the existing data to be modified
            this.userData = { ...modalData.existingUser };

            // Change the label on the button
            this.addUpdateBtnLabel = 'Update';
            this.dialogTitle = 'Update User';
            this.isEditing = true;
        }
    }

    get roles() {
        return this.userForm.get('roles');
    }

    buildRoles() {
        // get roles for existing userData
        let rolesExisting = [];
        if (Object.keys(this.userData).includes('user_roles')) {
            rolesExisting = Object.values(this.userData.user_roles);
        }
        const arr = this.user.roles.map(role => {
            if (rolesExisting && rolesExisting.includes(role.user_role_name)) {
                this.selectedRoleIds.push(role.id);
                this.userAssignedRoles.push(true);
                return this.formBuilder.control(true);
            } else {
                this.userAssignedRoles.push(false);
                // new user
                return this.formBuilder.control(false);
            }
        });
        return this.formBuilder.array(arr);
    }

    async ngOnInit() {
        this.authUser = await this.userService.getAuthUser();
        this.allRoles = await this.rolesApiService.getUserRoles();

        this.canManageCustomerUsers = await this.userService.userHasPrivs([UserPrivilegeEnum.MANAGE_CUSTOMER_USERS], this.authUser.customer_id);
        this.canManageSystemUsers = await this.userService.userHasPrivs([UserPrivilegeEnum.MANAGE_SYSTEM_USERS], this.authUser.customer_id);

        const rolesPrivilegesMatrixResult = await this.rolesApiService.getRolesPrivilegesMatrix();
        this.rolesPrivilegesMatrix = rolesPrivilegesMatrixResult['matrix'];

        if (this.customerId === this.SYSTEM_CUSTOMER_ID) {
            // user roles under system customer
            this.user.roles = this.allRoles['System'];
            this.canManageCurrentCustomerUsers = this.canManageSystemUsers;
        } else {
            // user roles for other customers
            this.user.roles = this.allRoles['Customer'];
            this.canManageCurrentCustomerUsers = this.canManageCustomerUsers;
        }

        this.userForm = this.formBuilder.group({
            roles: this.buildRoles(),
            'full_name': new FormControl(this.userData.full_name, [
                CustomValidators.blankField('Full Name')]),
            'email': new FormControl(this.userData.email, [
                CustomValidators.blankField('Email'), Validators.email])
        });

        this.userForm.get('email').valueChanges.subscribe(value => {
            // Transform input to lower case. Do not emit to prevent recursive call.
            this.userForm.get('email').setValue(value.toLowerCase(), { emitEvent: false });
        });
        this.userForm.get('roles').valueChanges.subscribe(value => {
            this.userForm.get('roles').setValue(value, { emitEvent: false });
            this.selectedRoleIds = this.userForm.value.roles
                .map((v, i) => v ? this.user.roles[i].id : null)
                .filter(v => v !== null);
        });
        if (this.isEditing) {
            this.userForm.get('email').disable();
        }
    }

    /**
     * Saves the updated values of the User
     */
    async save() {

        this.isLoading = true;

        try {
            const updateData = { ...this.userData, ...this.userForm.value };
            Utils.trimObjectValues(updateData);
            this.userForm.disable();

            for (const role of this.user.roles) {
                if (this.selectedRoleIds.includes(role.id)) {
                    role['selected'] = true;
                }
                else {
                    role['selected'] = false;
                }
            }
            updateData.user_roles = this.user.roles;

            if (this.modalData.existingUser) {
                const updateResp = await this.userApiService.updateUser({
                    user: updateData
                });
                const updatedUser = updateResp.user;

                // Pass the value returned from the server back to the UI
                this.activeModal.close({ newUserData: updatedUser, message: 'Update successful' });
                // If the logged-in user is changed we need to re-load
                if (this.userData.id === this.authUser.id) {
                    window.location.reload();
                }
            }
            else {
                // We are creating a new user for the customer

                // Add a customer ID
                updateData.customer_id = this.customerId;

                const createResp = await this.userApiService.createUser({
                    user: updateData
                });
                const newUser = createResp.user;

                // Pass the value returned from the server back to the UI
                this.activeModal.close({ newUserData: newUser, message: 'User added' });
            }
        }
        catch (ex) {
            this.activeModal.dismiss(ex.data.error);
        }

    }

    cancel() {
        this.activeModal.dismiss();
    }

    async resetPassword() {
        this.isLoading = true;
        try {
            const resp = await this.userApiService.resetUserPassword({
                userId: this.userData.id
            });
            this.isLoading = false;
            // Pass the value returned from the server back to the UI
            this.activeModal.close({ userData: null, message: resp.message });

        }
        catch (e) {
            this.activeModal.dismiss(e.data.error);

        }
    }

    formContainsChanges() {
        // Add dialog - changes do not need to be checked
        if (!this.isEditing) {
            return true;
        }

        // Edit dialog - changes to 'full_name' and 'roles' need to be checked
        // Role changes
        const roleChanges = !this.userAssignedRoles.every((element, index) => element === this.userForm.value['roles'][index]);

        // Full name change
        const fullNameChange = this.userForm.value['full_name'] !== this.userData.full_name;

        return roleChanges || fullNameChange;
    }

    isSaveButtonDisabled() {
        // User can not manage the current (other) customer user
        if (!this.canManageCurrentCustomerUsers && (this.userData.id !== this.authUser.id)) {
            return true;
        }

        return (this.isLoading || !this.formContainsChanges() || this.userForm.invalid || this.checkIfNewRolesLockOutUser());
    }

    public checkIfNewRolesLockOutUser() {
        /**
         * If user is editing himself, we make sure to that he does not remove
         * permissions to re-edit himself.
         *
         * This means if user is a system user, he needs to keep the
         * MANAGE_SYSTEM_USERS if he has it.
         * And if user is a customer user, he needs to keep the
         * MANAGE_CUSTOMER_USERS if he has it.
         *
         * Same check is run in the user_api.update_user method on management_api
         * it is written in a private function _check_if_new_roles_lock_out_user
         */
        // We do not need to perform any check if user is not editing himself.
        if (this.userData.id !== this.authUser.id) {
            return false;
        }

        // Here we just make sure that user does not lock himself out.
        // If user does not need to keep a specific privilege/permission
        // the value for keepsSystemMgmtPermission and keepsCustomerMgmtPermission
        // will be True by default.

        // If user needs to keep a specific permission the value of the correct
        // flag will be set to false.
        // For example if user needs to keep the  MANAGE_CUSTOMER_USERS
        // value of keepsCustomerMgmtPermission will be set to false.

        // If user is a system user he needs to keep the MANAGE_CUSTOMER_USERS privilege
        let keepsSystemMgmtPermission = !((this.authUser.customer_id === CustomerApiService.SYSTEM_CUSTOMER_ID) && this.canManageSystemUsers);
        // If user is not a system user he needs to keep the MANAGE_SYSTEM_USERS privilege
        let keepsCustomerMgmtPermission = !((this.authUser.customer_id !== CustomerApiService.SYSTEM_CUSTOMER_ID) && this.canManageCustomerUsers);

        // Here we loop though the newly selected roles and set the values of
        // keepsSystemMgmtPermission or keepsCustomerMgmtPermission
        // to true as soon as a role is found that has the corresponding permission/privilege.
        for (let i = 0; i < this.selectedRoleIds.length; i++) {
            // Early break if requirement satisfied
            if (keepsSystemMgmtPermission && keepsCustomerMgmtPermission) {
                break;
            }
            const role = this.selectedRoleIds[i];

            // Only do the check if not already True - optimization.
            if (!keepsSystemMgmtPermission) {
                // Check if current role is listed as a role with the desired privilege
                keepsSystemMgmtPermission = (this.rolesPrivilegesMatrix[UserPrivilegeEnum.MANAGE_SYSTEM_USERS].includes(role));
            }
            // Only do the check if not already True
            if (!keepsCustomerMgmtPermission) {
                // Check if current role is listed as a role with the desired privilege
                keepsCustomerMgmtPermission = (this.rolesPrivilegesMatrix[UserPrivilegeEnum.MANAGE_CUSTOMER_USERS].includes(role));
            }
        }

        // If both are true the new roles do not lock out the user.
        return !(keepsSystemMgmtPermission && keepsCustomerMgmtPermission);
    }
}
